import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { fill as fillPDF, SCHEMA_TO_PDF_FIELD, PAGE_1_SCHEMA_FIELDS, STEP_ANCHOR_FIELDS } from "./fill.js";
import templateUrl from "./template.pdf";
import { PRESETS } from "./presets.js";
import { formSchema, defaultValues } from "./schema.js";
import { twoPlans, fourPlans, threeResults, selectItems } from "./options.js";
import { STEPS, STEP_FIELDS, CORE_SUBJECTS, ARTS_ROWS } from "./steps.js";
import { useReportForm } from "@/reports/shared_utils/useReportForm";
import { PillSelector } from "@/reports/report_components/PillSelector";
import { SectionHeading } from "@/reports/report_components/SectionHeading";
import { FormFieldGrid } from "@/reports/report_components/FormFieldGrid";
import { AICommentButton } from "@/reports/report_components/AICommentButton";
import { SubjectRow } from "@/reports/report_components/SubjectRow";
import { ArtsRow } from "@/reports/report_components/ArtsRow";
import { StepIndicator } from "@/reports/report_components/StepIndicator";
import { ReportFormShell } from "@/reports/report_components/ReportFormShell";
import { LSWH_CONFIG, SUBJECTS_CONFIG } from "./aiConfig";

const FORM_CONFIG = {
  formSchema,
  defaultValues,
  fillPDF,
  templateUrl,
  STEPS,
  STEP_FIELDS,
  STEP_ANCHOR_FIELDS,
  SCHEMA_TO_PDF_FIELD,
  getPageIndex: (f) => PAGE_1_SCHEMA_FIELDS.has(f) ? 0 : 1,
  getInitialPage: (s) => s <= 4 ? 1 : 2,
  reportTypeKey: 'pgrc1to6_2023_07',
};

export const DemoReportCardProgressGr16202307 = ({ reportId, backUrl, contextInfo, defaultsGroupId, onRequestSave, readOnly = false, logos = [], logosVersion = null, isOwner = false, ownSignature = null } = {}) => {
  const hook = useReportForm(FORM_CONFIG, { reportId, defaultsGroupId, groupGrade: contextInfo?.grade, logos, logosVersion, isOwner, ownSignature });

  const renderStep = (form, currentStep) => {
    const { control } = form;
    const wrap = (children) => (
      <div className="rounded-2xl border border-border bg-card shadow-sm p-6">{children}</div>
    );

    if (currentStep === 1) {
      return wrap(
        <div className="space-y-4">
          <SectionHeading title="Student Information" />
          <FormFieldGrid
            fields={[
              { name: "studentName", label: "Student Name" },
              { name: "oen",         label: "OEN" },
              { name: "date",        label: "Date" },
              { name: "grade",       label: "Grade" },
              { name: "teacher",     label: "Teacher" },
            ]}
            control={control}
          />
        </div>
      );
    }

    if (currentStep === 2) {
      return wrap(
        <div className="space-y-4">
          <SectionHeading title="School Information" />
          <FormFieldGrid
            fields={[
              { name: "board",     label: "Board" },
              { name: "school",    label: "School" },
              { name: "address1",  label: "Address Line 1" },
              { name: "address2",  label: "Address Line 2" },
              { name: "principal", label: "Principal" },
              { name: "telephone", label: "Telephone" },
            ]}
            control={control}
          />
        </div>
      );
    }

    if (currentStep === 3) {
      return wrap(
        <div className="space-y-4">
          <SectionHeading title="Attendance" />
          <FormFieldGrid
            fields={[
              { name: "daysAbsent",      label: "Days Absent (this term)", inputMode: "numeric" },
              { name: "totalDaysAbsent", label: "Total Days Absent",        inputMode: "numeric" },
              { name: "timesLate",       label: "Times Late (this term)",   inputMode: "numeric" },
              { name: "totalTimesLate",  label: "Total Times Late",         inputMode: "numeric" },
            ]}
            control={control}
            gridClassName="grid grid-cols-2 gap-6"
          />
          <FormField
            control={control}
            name="boardInformation1"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Board Information (Page 1)</FormLabel>
                <FormControl>
                  <Textarea {...field} className="min-h-[80px]" />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      );
    }

    if (currentStep === 4) {
      const SKILL_LABELS = {
        responsibility:  "Responsibility",
        organization:    "Organization",
        independentWork: "Independent Work",
        collaboration:   "Collaboration",
        initiative:      "Initiative",
        selfRegulation:  "Self-Regulation",
      };
      return wrap(
        <div className="space-y-5">
          <SectionHeading title="Learning Skills & Work Habits" />
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(SKILL_LABELS).map(([name, label]) => (
              <div key={name} className="rounded-xl border border-border bg-card p-4 space-y-2">
                <p className="font-semibold text-foreground text-sm">{label}</p>
                <FormField
                  control={control}
                  name={name}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <PillSelector
                          value={field.value}
                          onChange={field.onChange}
                          options={selectItems}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            ))}
          </div>
          <AICommentButton
            config={LSWH_CONFIG}
            form={form}
            fieldName="strengthsNextStepsLSWH"
            reportId={reportId}
            label="Strengths & Next Steps - Learning Skills"
          />
        </div>
      );
    }

    if (currentStep === 5) {
      return wrap(
        <div className="space-y-4">
          <SectionHeading title="Subject Grades" />
          <div className="space-y-3">
            {CORE_SUBJECTS.map((subject) => (
              <SubjectRow key={subject.planField} control={control} {...subject} resultOptions={threeResults} />
            ))}
          </div>
        </div>
      );
    }

    if (currentStep === 6) {
      return wrap(
        <div className="space-y-5">
          <SectionHeading title="Arts & Final Comments" />

          <FormField
            control={control}
            name="artsHeader"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Arts Header</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. The Arts" {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          <div>
            <p className="text-sm font-semibold text-muted-foreground mb-2">Arts Subjects</p>
            <div className="space-y-3">
              {ARTS_ROWS.map((row) => (
                <ArtsRow key={row.nameField} control={control} {...row} planOptions={fourPlans} resultOptions={threeResults} />
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-4 space-y-3">
            <p className="font-semibold text-foreground">Optional Studies</p>
            <FormField
              control={control}
              name="optionalStudies"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Subject Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Islamic Studies" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Plan</p>
              <FormField
                control={control}
                name="optionalStudiesPlan"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <PillSelector value={field.value} onChange={field.onChange} options={fourPlans} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Result</p>
              <FormField
                control={control}
                name="optionalStudiesResult"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <PillSelector value={field.value} onChange={field.onChange} options={threeResults} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>

          <AICommentButton
            config={SUBJECTS_CONFIG}
            form={form}
            fieldName="strengthsNextStepsSubjects"
            reportId={reportId}
            label="Strengths & Next Steps - Subjects"
          />

          <FormField
            control={control}
            name="boardInformation2"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Board Information (Page 2)</FormLabel>
                <FormControl>
                  <Textarea {...field} className="min-h-[80px]" />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <ReportFormShell
      hook={hook}
      title="Ontario Progress Report Card"
      reportId={reportId}
      backUrl={backUrl}
      contextInfo={contextInfo}
      defaultsGroupId={defaultsGroupId}
      onRequestSave={onRequestSave}
      STEPS={STEPS}
      PRESETS={PRESETS}
      defaultPageCount={2}
      StepIndicator={StepIndicator}
      stepIndicatorProps={{ steps: STEPS }}
      renderStep={renderStep}
      formKey={hook.currentStep}
      readOnly={readOnly}
    />
  );
};
